// 원격지 코드 입력 칸 + **최근 접속 목록**(첫 화면 즉석 접속 · 로그인 후 [연결] 화면 — 같은 목록). 저장·정리 규칙은 `recentCodes.ts`.
//
// - 칸을 누르거나 ▾ 를 누르면 최근 코드가 펼쳐진다(입력한 숫자로 좁혀진다). 고르면 칸이 채워지고 비밀번호 칸으로 넘어간다.
// - 각 항목의 × 로 목록에서 지운다(남의 PC 코드를 지워 두고 싶을 때).
// - 세션은 **다른 창**에서 열려 거기서 성공을 기록하므로, `storage` 이벤트로 목록을 새로 읽는다(창을 다시 열지 않아도 보인다).
import { useEffect, useMemo, useState } from 'react';
import { forgetCode, loadRecentCodes, RECENT_CODES_KEY } from '../recentCodes';
import type { RecentCode } from '../recentCodes';

/** "3분 전"·"어제" 꼴의 짧은 시각 표시. */
function ago(at: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return '방금';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}분 전`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}시간 전`;
  const d = Math.round(h / 24);
  return d === 1 ? '어제' : `${d}일 전`;
}

export function RecentCodeField({
  value,
  onChange,
  onPicked,
  onEnter,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  /** 목록에서 골랐다(다음 칸으로 포커스를 넘기는 데 쓴다). */
  onPicked?: () => void;
  /** 칸에서 Enter — 바로 접속하는 화면([연결])이 쓴다. */
  onEnter?: () => void;
  placeholder?: string;
}) {
  const [list, setList] = useState<RecentCode[]>(() => loadRecentCodes());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const reload = (e?: StorageEvent) => {
      if (!e || e.key === null || e.key === RECENT_CODES_KEY) setList(loadRecentCodes());
    };
    const onFocus = () => reload();
    window.addEventListener('storage', reload);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', reload);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // 입력한 숫자로 좁힌다(구분자 무시). 비어 있으면 전부.
  const shown = useMemo(() => {
    const q = value.replace(/[\s-]/g, '');
    return q ? list.filter((e) => e.code.replace(/-/g, '').includes(q)) : list;
  }, [list, value]);

  const pick = (code: string) => {
    onChange(code);
    setOpen(false);
    onPicked?.();
  };

  return (
    <div className="recent-code">
      <div className="recent-code-row">
        <input
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter' && onEnter) {
              setOpen(false);
              onEnter();
            }
          }}
          placeholder={placeholder}
          aria-label="원격지 코드"
          autoComplete="off"
        />
        {list.length > 0 && (
          <button
            type="button"
            className="ghost recent-code-toggle"
            title="최근 접속한 코드"
            aria-label="최근 접속한 코드"
            // 누르는 순간 입력 칸이 blur 로 목록을 닫지 않게
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setOpen((o) => !o)}
          >
            ▾
          </button>
        )}
      </div>
      {open && shown.length > 0 && (
        <ul className="recent-code-list" role="listbox" aria-label="최근 접속">
          {shown.map((e) => (
            <li key={e.code} role="option" aria-selected={e.code === value}>
              <button
                type="button"
                className="recent-code-item"
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => pick(e.code)}
              >
                <span className="recent-code-code">{e.code}</span>
                <span className="recent-code-at">{ago(e.at)}</span>
              </button>
              <button
                type="button"
                className="recent-code-del"
                title="목록에서 지우기"
                aria-label={`${e.code} 목록에서 지우기`}
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => setList(forgetCode(e.code))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
